// Fixed bottom nav bar shown only on mobile (< md). Gives one-tap access to
// the four most-used surfaces so users don't have to open the hamburger menu.
// Mount once at the app root; it auto-pads the page bottom via a sibling
// spacer (`h-16`) we render alongside.
//
// Simplified to mirror the new "everything funnels into the home chat"
// nav story:
//   Home (chat) · Roadmap · Library · Account
// Learn / Build / Hack are no longer their own slots because they're
// now MODES inside the chat shell on the home page.
import { Home, Compass, Library as LibraryIcon, UserCircle2, Sparkle, Bot } from "lucide-react";
import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useLevelOneComplete } from "@/hooks/useLevelOneComplete";

const BASE_ITEMS = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/roadmap", label: "Learn", icon: Compass, end: false },
  { to: "/agents", label: "Agents", icon: Bot, end: false },
  { to: "/library", label: "Library", icon: LibraryIcon, end: false },
];

const MobileBottomNav = () => {
  const { user } = useAuth();
  const levelOneDone = useLevelOneComplete();
  // Fourth slot adapts to auth state. Signed-in: account. Guest who
  // finished Level 1: nudge to Join (pricing). Everyone else: account.
  const fourth = user
    ? { to: "/account", label: "Account", icon: UserCircle2, end: false }
    : levelOneDone
      ? { to: "/pricing", label: "Join", icon: Sparkle, end: false }
      : { to: "/account", label: "Account", icon: UserCircle2, end: false };
  const items = [...BASE_ITEMS, fourth];
  // Bar now has 5 slots — keep grid in sync via items.length below.

  return (
    <>
      {/* Spacer so page content doesn't sit under the bar */}
      <div aria-hidden className="md:hidden h-16" />
      <nav
        aria-label="Primary mobile navigation"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]"
      >
        <ul className="grid grid-cols-5">
          {items.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono transition-colors",
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                  )
                }
              >
                <Icon className="w-5 h-5" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export default MobileBottomNav;